import { useState } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/stores/auth.store';
import { authService } from '@/services/auth.service';
import { useToast } from '@/hooks/use-toast';

const schema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type FormData = z.infer<typeof schema>;

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { setToken, setUser } = useAuthStore();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const invitationToken = searchParams.get('invitation');
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  async function onSubmit(data: FormData) {
    setError('');
    setIsLoading(true);
    try {
      const res = await authService.login(data);
      const responseData = res.data.data;
      const { access_token, user } = responseData;

      // Handle approval statuses
      if (user?.approval_status === 'pending') {
        navigate('/pending-approval');
        return;
      }
      if (user?.approval_status === 'blocked') {
        navigate('/blocked');
        return;
      }

      // Handle case where token is null
      if (!access_token) {
        setError('Login failed. Please try again.');
        return;
      }

      setToken(access_token);
      setUser(user);

      // If logging in with an invitation token, accept it now
      if (invitationToken) {
        try {
          const invRes = await authService.acceptInvitation(invitationToken);
          const wsName = invRes.data.data?.workspace_name;
          toast({
            title: 'Invitation accepted!',
            description: `You've joined ${wsName ?? 'the workspace'}.`,
          });
        } catch {
          // Invitation may already be accepted or expired — don't block login
        }
      }

      navigate(invitationToken ? '/dashboard' : from, { replace: true });
    } catch (err: unknown) {
      const axiosError = err as { response?: { status?: number; data?: { error?: { message?: string; code?: string }; data?: { approval_status?: string; email_verified?: boolean } } } };
      const errData = axiosError.response?.data;

      // Handle approval status from error response
      if (errData?.data?.approval_status === 'pending') {
        navigate('/pending-approval');
        return;
      }
      if (errData?.data?.approval_status === 'blocked') {
        navigate('/blocked');
        return;
      }

      // Handle email not verified (403 EMAIL_NOT_VERIFIED)
      if (
        axiosError.response?.status === 403 &&
        errData?.error?.code === 'EMAIL_NOT_VERIFIED'
      ) {
        setError(
          'Your email address has not been verified. Please check your inbox for a verification link.'
        );
        return;
      }
      if (errData?.data?.email_verified === false) {
        setError(
          'Please verify your email before logging in.'
        );
        return;
      }

      setError(errData?.error?.message || 'Invalid email or password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <AuthLayout>
      <Card className="border-0 bg-white/80 shadow-lg shadow-purple-500/5 backdrop-blur-sm dark:bg-card/80 rounded-xl">
        <CardContent className="p-6 sm:p-8">
          {/* Header */}
          <div className="mb-6 text-center">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-brand-primary to-purple-500 bg-clip-text text-transparent">
              Welcome back
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Enter your credentials to access your account
            </p>
          </div>

          {/* Error alert */}
          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800/50 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
                {...register('email')}
              />
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-brand-primary hover:text-brand-primary/80 hover:underline"
                >
                  Forgot your password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="pr-10"
                  {...register('password')}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-destructive">{errors.password.message}</p>
              )}
            </div>

            {/* Submit */}
            <Button
              type="submit"
              className="h-10 w-full rounded-md bg-gradient-to-r from-brand-primary to-purple-600 text-white shadow-md hover:shadow-lg hover:opacity-90 transition-all"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>

          {/* Footer links */}
          <div className="mt-6 space-y-3 text-center">
            <p className="text-sm text-muted-foreground">
              Don&apos;t have an account?{' '}
              <Link
                to="/signup"
                className="font-medium text-brand-primary hover:text-brand-primary/80 hover:underline"
              >
                Sign up
              </Link>
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-3 w-3" />
              Back to home
            </Link>
          </div>
        </CardContent>
      </Card>
    </AuthLayout>
  );
}
